/**
 * Обвязка экрана: заголовок с инструментами и требование выбранного бренда.
 *
 * Половина разделов кабинета осмысленна только внутри бренда, и каждый из них
 * иначе повторял бы одну и ту же лесенку «область грузится → область не
 * загрузилась → брендов нет → бренд не выбран». Повторённая четыре раза, она
 * рано или поздно разъедется, и один экран начнёт показывать ноль там, где
 * другой честно говорит, что выбирать нечего.
 */

import type { ReactNode } from 'react';

import { useScope } from '../scope';
import { t } from '../i18n';
import { Empty, ErrorNotice, Skeleton } from './state';
import { NeverMeasured, pickBrand } from './screen-states';
import { useAccess } from '../session';

export function ScreenHead({ title, tools }: { title: string; tools?: ReactNode }) {
  return (
    <div className="screen-head">
      <h1>{title}</h1>
      {tools ? <div className="head-tools">{tools}</div> : null}
    </div>
  );
}

/**
 * Экран, которому нужен бренд. Пока бренда нет, показывается не пустая таблица,
 * а причина: брендов нет вовсе или бренд не выбран — это разные вещи.
 */
export function WithBrand({
  title,
  tools,
  children,
}: {
  title: string;
  tools?: ReactNode;
  children: (brandId: string) => ReactNode;
}) {
  const scope = useScope();
  const access = useAccess();
  const head = <ScreenHead title={title} tools={scope.brandId ? tools : undefined} />;

  if (scope.state.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={2} />
      </>
    );
  }

  if (scope.state.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice error={scope.state.error} />
      </>
    );
  }

  // Живых брендов нет — значит смотреть нечего, даже если в архиве лежит
  // десяток: замеры по архивным не идут.
  if (scope.liveBrands.length === 0) {
    return (
      <>
        {head}
        <NeverMeasured
          action={
            access.can('brands.write') ? (
              <a className="btn btn-primary" href="/brands/new">
                {t('screen.new-brand')}
              </a>
            ) : undefined
          }
        />
      </>
    );
  }

  if (!scope.brandId) {
    return (
      <>
        {head}
        <Empty eyebrow={t('screen.pick.eyebrow')} title={t('screen.pick.title')}>
          {t('screen.pick.body')} {pickBrand()}
        </Empty>
      </>
    );
  }

  // Смена бренда — другой экран, а не обновление этого: пересоздаём поддерево,
  // чтобы числа прошлого бренда не пережили переключение ни на кадр.
  return (
    <>
      {head}
      <div key={scope.brandId}>{children(scope.brandId)}</div>
    </>
  );
}
